import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@mui/styles';
import {
  Typography,
  Button,
  Box,
  Divider,
  CircularProgress,
  DialogActions,
} from '@mui/material';
import { withTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import GROUP_TYPES from '../common/group-types';
import BOX_TYPE from '../enum/box_type';
import ColumnVisualization from './ColumnVisualization';

const FOUR_ROW_TYPES = [BOX_TYPE.Cool];
const SIX_ROW_TYPES = [BOX_TYPE.COOL_12, BOX_TYPE.A12, BOX_TYPE.V12];
const ELEVEN_ROW_MAIN_UNIT_TYPES = [BOX_TYPE.M41, BOX_TYPE.C28, BOX_TYPE.HD33];
const ELEVEN_ROW_EXPANSION_TYPES = [BOX_TYPE.C28, BOX_TYPE.HD33];
const MIDDLE_SIX_ROW_TYPES = [BOX_TYPE.C30, BOX_TYPE.C28];

const styles = (theme) => ({
  root: {
    padding: theme.spacing(3),
    paddingBottom: theme.spacing(10), // Add bottom padding for fixed buttons
    position: 'relative',
    minHeight: '100%',
  },
  columnSection: {
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
    border: '1px solid #e0e0e0',
    borderRadius: '4px',
  },
  columnGroup: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  columnChip: {
    margin: theme.spacing(0.5),
  },
  directionArrow: {
    margin: theme.spacing(1),
    fontSize: '20px',
    fontWeight: 'bold',
  },
  groupSelector: {
    minWidth: 200,
    marginBottom: theme.spacing(2),
  },
  saveButton: {
    marginTop: theme.spacing(2),
  },
  buttonContainer: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    padding: theme.spacing(2),
    backgroundColor: '#fff',
    borderTop: '1px solid #e0e0e0',
  },
  primaryButton: {
    marginLeft: theme.spacing(1),
  },
  expansionLabel: {
    fontWeight: 'bold',
    marginBottom: theme.spacing(1),
  },
  mainUnitLabel: {
    fontWeight: 'bold',
    marginBottom: theme.spacing(1),
    color: theme.palette.primary.main,
  },
});

class ColumnMappingTab extends Component {
  constructor(props) {
    super(props);
    this.state = {
      columnMappings: {},
      loading: false,
      saving: false,
      expansions: [],
      columns: {
        leftExpansions: [],
        mainUnit: [],
        rightExpansions: [],
      },
      allColumns: [],
      mainUnitColumnCount: 5, // Default, will be updated from box data
    };
  }

  componentDidMount() {
    this.initializeColumnMappings();
    this.initializeFromProps();
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.locationId !== this.props.locationId ||
      prevProps.expansions !== this.props.expansions ||
      prevProps.boxTypeId !== this.props.boxTypeId ||
      prevProps.columnMappings !== this.props.columnMappings
    ) {
      this.initializeColumnMappings();
      this.initializeFromProps();
    }
  }

  initializeColumnMappings = () => {
    const { columnMappings } = this.props;
    
    if (columnMappings && Array.isArray(columnMappings) && columnMappings.length > 0) {
      // Transform API response to state format
      const mappings = {};
      columnMappings.forEach((mapping) => {
        // Filter out invalid group_type_ids (like 'none', null, undefined, or empty strings)
        if (mapping && mapping.group_type_id && 
            mapping.group_type_id !== 'none' && 
            mapping.group_type_id !== 'Unassigned' &&
            typeof mapping.group_type_id === 'string' &&
            mapping.group_type_id.trim() !== '' &&
            mapping.column_id) {
          if (!mappings[mapping.group_type_id]) {
            mappings[mapping.group_type_id] = [];
          }
          mappings[mapping.group_type_id].push(mapping.column_id);
        }
      });
      this.setState({ columnMappings: mappings, loading: false });
    } else {
      // No mappings found, set empty state
      this.setState({ columnMappings: {}, loading: false });
    }
  }

  getColumnPrefix = (columnId) => {
    if (!columnId || typeof columnId !== 'string') {
      return '';
    }

    const match = columnId.match(/^([A-Z]+)/i);
    return match ? match[1].toUpperCase() : '';
  };

  normalizeColumnMappingsToCurrentColumns = (columnStructure) => {
    const rawColumnMappings = Array.isArray(this.props.columnMappings)
      ? this.props.columnMappings
      : [];

    const allColumns = [
      ...columnStructure.leftExpansions.flatMap((exp) => exp.columns),
      ...columnStructure.mainUnit,
      ...columnStructure.rightExpansions.flatMap((exp) => exp.columns),
    ];

    const normalizedMappings = {};

    rawColumnMappings.forEach((mapping) => {
      if (
        !mapping ||
        !mapping.group_type_id ||
        mapping.group_type_id === 'none' ||
        mapping.group_type_id === 'Unassigned' ||
        typeof mapping.group_type_id !== 'string' ||
        mapping.group_type_id.trim() === '' ||
        !mapping.column_id
      ) {
        return;
      }

      const exactMatch = allColumns.find((column) => column.id === mapping.column_id);
      const prefix = this.getColumnPrefix(mapping.column_id);
      const prefixMatch = !exactMatch && prefix
        ? allColumns.find((column) => this.getColumnPrefix(column.id) === prefix)
        : null;
      const normalizedColumnId = (exactMatch || prefixMatch || { id: mapping.column_id }).id;

      if (!normalizedMappings[mapping.group_type_id]) {
        normalizedMappings[mapping.group_type_id] = [];
      }

      if (!normalizedMappings[mapping.group_type_id].includes(normalizedColumnId)) {
        normalizedMappings[mapping.group_type_id].push(normalizedColumnId);
      }

    });

    return normalizedMappings;
  };

  initializeFromProps = () => {
    const { expansions, boxTypeId } = this.props;
    
    const mainUnitColumnCount = this.getMainUnitColumnCount(boxTypeId);
    
    if (expansions) {
      const expansionsList = Array.isArray(expansions) ? expansions : [];
      
      this.setState({ 
        expansions: expansionsList, 
        mainUnitColumnCount,
      });
      this.buildColumnHierarchy(expansionsList, mainUnitColumnCount);
    }
  };

  getMainUnitColumnCount = (boxTypeId) => {
    if ([BOX_TYPE.Cool, BOX_TYPE.COOL_12, BOX_TYPE.A12, BOX_TYPE.V12].includes(boxTypeId)) {
      return 2;
    }

    if ([BOX_TYPE.M22, BOX_TYPE.M24, BOX_TYPE.HD36, BOX_TYPE.C30, BOX_TYPE.C28, BOX_TYPE.HD33].includes(boxTypeId)) {
      return 3;
    }

    return 5;
  };

  getMainUnitRowEnd = (boxTypeId) => {
    if (FOUR_ROW_TYPES.includes(boxTypeId)) {
      return 4;
    }

    if (SIX_ROW_TYPES.includes(boxTypeId)) {
      return 6;
    }

    if (ELEVEN_ROW_MAIN_UNIT_TYPES.includes(boxTypeId)) {
      return 11;
    }

    return 12;
  };

  buildColumnHierarchy = (expansions, mainUnitColumnCount = 5) => {
    const rowEndByBoxType = this.getMainUnitRowEnd(this.props.boxTypeId);
    const expansionElevenRowTypes = ELEVEN_ROW_EXPANSION_TYPES;
    const expansionMiddleSixRowTypes = MIDDLE_SIX_ROW_TYPES;
    const expansionFourRowTypes = FOUR_ROW_TYPES;
    const expansionSixRowTypes = SIX_ROW_TYPES;

    // This will build the column hierarchy based on expansions
    // Structure: Left Expansions <- Main Unit -> Right Expansions
    // Column naming: Left uses double letters (AA, BB, CC...), Right uses single letters (A, B, C...)
    
    const columnStructure = {
      leftExpansions: [],
      mainUnit: [],
      rightExpansions: [],
    };

    // Main unit column count is passed as parameter (fetched from box data)
    
    // Match Assign Prescription unit ordering:
    // - group by direction first
    // - preserve API/source order within each side
    // - reverse the left side before rendering around the main unit
    // Fallback to box_position only when direction is missing.
    const leftExpansions = expansions.filter((exp) =>
      exp.direction === 1 ||
      ((exp.direction === null || exp.direction === undefined || exp.direction === '') &&
        exp.box_position < 0)
    );

    const rightExpansions = expansions.filter((exp) =>
      exp.direction === 2 ||
      ((exp.direction === null || exp.direction === undefined || exp.direction === '') &&
        exp.box_position > 0)
    );

    // Build left expansion columns (double letters, going backwards from main unit)
    const orderedLeftExpansions = [...leftExpansions].reverse();
    const totalLeftColumns = orderedLeftExpansions.reduce(
      (sum, expansion) => sum + (expansion.bin_column_count || 0),
      0
    );
    let processedLeftColumns = 0;

    orderedLeftExpansions.forEach((expansion) => {
      const columnCount = expansion.bin_column_count || 0;
      const expansionColumns = [];
      const leftColumnOffset =
        totalLeftColumns - processedLeftColumns - columnCount;
      
      // Generate double letter columns (AA, BB, CC, DD, EE, FF, etc.)
      for (let i = 0; i < columnCount; i++) {
        const letterIndex = 65 + leftColumnOffset + i; // 65 = 'A'
        const letter1 = String.fromCharCode(letterIndex);
        const letter2 = String.fromCharCode(letterIndex);
        const expansionTypeId = expansion && expansion.expansion_id;
        let defaultExpansionRowEnd = 12;
        if (expansionFourRowTypes.includes(expansionTypeId)) {
          defaultExpansionRowEnd = 4;
        } else if (expansionSixRowTypes.includes(expansionTypeId)) {
          defaultExpansionRowEnd = 6;
        } else if (expansionElevenRowTypes.includes(expansionTypeId)) {
          defaultExpansionRowEnd = 11;
        }
        const columnRowEnd =
          expansionMiddleSixRowTypes.includes(expansionTypeId) && columnCount === 3 && i === 1
            ? 6
            : defaultExpansionRowEnd;
        expansionColumns.push({
          id: `${letter1}${letter2}1-${letter1}${letter2}${columnRowEnd}`, // e.g., "AA1-AA12"
          column: letter1 + letter2,
          expansionId: expansion.id,
          expansionPosition: expansion.box_position,
          isMainUnit: false,
          side: 'left',
        });
      }
      
      columnStructure.leftExpansions.push({
        expansion,
        columns: expansionColumns,
      });
      
      processedLeftColumns += columnCount;
    });

    // Build main unit columns (single letters: A, B, C, D, E, etc.)
    const mainUnitColumns = [];
    for (let i = 0; i < mainUnitColumnCount; i++) {
      const letter = String.fromCharCode(65 + i); // A, B, C, D, E...
      mainUnitColumns.push({
        id: `${letter}1-${letter}${rowEndByBoxType}`, // e.g., "A1-A12"
        column: letter,
        expansionId: null,
        expansionPosition: 0,
        isMainUnit: true,
        side: 'main',
      });
    }
    columnStructure.mainUnit = mainUnitColumns;

    // Build right expansion columns (single letters, continuing from main unit)
    let rightColumnOffset = mainUnitColumnCount;
    rightExpansions.forEach((expansion) => {
      const columnCount = expansion.bin_column_count || 0;
      const expansionColumns = [];
      
      // Generate single letter columns (F, G, H, I, J, K, etc.)
      for (let i = 0; i < columnCount; i++) {
        const letterIndex = 65 + rightColumnOffset + i; // Continue from main unit
        const letter = String.fromCharCode(letterIndex);
        const expansionTypeId = expansion && expansion.expansion_id;
        let defaultExpansionRowEnd = 12;
        if (expansionFourRowTypes.includes(expansionTypeId)) {
          defaultExpansionRowEnd = 4;
        } else if (expansionSixRowTypes.includes(expansionTypeId)) {
          defaultExpansionRowEnd = 6;
        } else if (expansionElevenRowTypes.includes(expansionTypeId)) {
          defaultExpansionRowEnd = 11;
        }
        const columnRowEnd =
          expansionMiddleSixRowTypes.includes(expansionTypeId) && columnCount === 3 && i === 1
            ? 6
            : defaultExpansionRowEnd;
        expansionColumns.push({
          id: `${letter}1-${letter}${columnRowEnd}`, // e.g., "F1-F12"
          column: letter,
          expansionId: expansion.id,
          expansionPosition: expansion.box_position,
          isMainUnit: false,
          side: 'right',
        });
      }
      
      columnStructure.rightExpansions.push({
        expansion,
        columns: expansionColumns,
      });
      
      rightColumnOffset += columnCount;
    });

    const normalizedColumnMappings = this.normalizeColumnMappingsToCurrentColumns(columnStructure);

    this.setState({ 
      columns: columnStructure,
      columnMappings: normalizedColumnMappings,
      // Flatten all columns for easier access
      allColumns: [
        ...columnStructure.leftExpansions.flatMap(exp => exp.columns),
        ...columnStructure.mainUnit,
        ...columnStructure.rightExpansions.flatMap(exp => exp.columns),
      ]
    });
  };

  fetchColumnMappings = () => {
    const { locationId } = this.props;
    
    if (!locationId) {
      this.setState({ columnMappings: {}, loading: false });
      return;
    }

    this.setState({ loading: true });

    const user = JSON.parse(sessionStorage.getItem('user'));
    if (!user || !user.signInUserSession || !user.signInUserSession.idToken) {
      this.setState({ columnMappings: {}, loading: false });
      return;
    }

    fetch(process.env.REACT_APP_BASEURL + `locations/${locationId}/column-mappings`, {
      method: 'GET',
      headers: {
        Authorization: 'Bearer ' + user.signInUserSession.idToken.jwtToken,
        'Content-Type': 'application/json',
      },
    })
      .then((response) => response.json())
      .then((data) => {
        if (data && data.statusCode === 200 && data.mappings) {
          // Transform API response to state format
          const mappings = {};
          data.mappings.forEach((mapping) => {
            if (mapping && mapping.group_type_id && 
                mapping.group_type_id !== 'none' && 
                mapping.group_type_id !== 'Unassigned' &&
                typeof mapping.group_type_id === 'string' &&
                mapping.group_type_id.trim() !== '' &&
                mapping.column_id) {
              if (!mappings[mapping.group_type_id]) {
                mappings[mapping.group_type_id] = [];
              }
              mappings[mapping.group_type_id].push(mapping.column_id);
            }
          });
          this.setState({ columnMappings: mappings, loading: false });
        } else {
          this.setState({ columnMappings: {}, loading: false });
        }
      })
      .catch((error) => {
        console.error('Error fetching column mappings:', error);
        this.setState({ columnMappings: {}, loading: false });
      });
  };

  handleColumnGroupChange = (columnId, groupTypeId) => {
    const { columnMappings } = this.state;
    const { isSuperAdmin, userGroupType } = this.props;
    const targetPrefix = this.getColumnPrefix(columnId);

    // Check if user can assign to this group
    if (!isSuperAdmin && groupTypeId !== userGroupType) {
      toast.error(this.props.t('Error.CannotAssignToOtherGroup'));
      return;
    }

    // Remove column from all groups first
    Object.keys(columnMappings).forEach((groupId) => {
      columnMappings[groupId] = columnMappings[groupId].filter(
        (id) => id !== columnId && this.getColumnPrefix(id) !== targetPrefix
      );
    });

    // Add to selected group if not "None"
    if (groupTypeId && groupTypeId !== 'none') {
      if (!columnMappings[groupTypeId]) {
        columnMappings[groupTypeId] = [];
      }
      columnMappings[groupTypeId].push(columnId);
    }

    this.setState({ columnMappings });
  };

  handleSave = () => {
    const { locationId } = this.props;
    const { columnMappings } = this.state;

    if (!locationId) {
      toast.error(this.props.t('Error.NoLocationSelected'));
      return;
    }

    this.setState({ saving: true });

    // Transform state to API format
    // Note: location_id is not needed in payload - API uses locationId from route parameter
    const mappings = [];
    Object.keys(columnMappings).forEach((groupTypeId) => {
      columnMappings[groupTypeId].forEach((columnId) => {
        mappings.push({
          column_id: columnId,
          group_type_id: groupTypeId,
        });
      });
    });

    const user = JSON.parse(sessionStorage.getItem('user'));
    if (!user || !user.signInUserSession || !user.signInUserSession.idToken) {
      toast.error(this.props.t('Error.SavingColumnMappings'));
      this.setState({ saving: false });
      return;
    }

    fetch(process.env.REACT_APP_BASEURL + `locations/${locationId}/column-mappings`, {
      method: 'POST',
      withCredentials: true,
      headers: {
        Authorization:
          'Bearer ' + user.signInUserSession.idToken.jwtToken,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ mappings }),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data && data.statusCode === 200) {
          toast.success(this.props.t('Success.ColumnMappingsSaved'));
          this.setState({ saving: false });
          // Refresh column mappings from API after successful save
          this.fetchColumnMappings();
          // If parent has a callback to refresh box data, call it
          if (this.props.onColumnMappingsSaved) {
            this.props.onColumnMappingsSaved();
          }
        } else {
          throw new Error(data.message || 'Failed to save column mappings');
        }
      })
      .catch((error) => {
        console.error('Error saving column mappings:', error);
        toast.error(error.message || this.props.t('Error.SavingColumnMappings'));
        this.setState({ saving: false });
      });
  };

  renderColumnVisualization = () => {
    const { isSuperAdmin, userGroupType, boxTypes, boxTypeId } = this.props;
    const { columns, columnMappings } = this.state;

    return (
      <ColumnVisualization
        columns={columns}
        columnMappings={columnMappings}
        isSuperAdmin={isSuperAdmin}
        userGroupType={userGroupType}
        boxTypeId={boxTypeId}
        locationId={this.props.locationId}
        boxTypes={boxTypes || []}
        onColumnGroupChange={this.handleColumnGroupChange}
      />
    );
  };

  getGroupName = (groupTypeId) => {
    if (groupTypeId === GROUP_TYPES.Pharmacy) return 'Pharmacy';
    if (groupTypeId === GROUP_TYPES.Retail) return 'Optical';
    if (groupTypeId === GROUP_TYPES.eCommerce) return 'eCommerce';
    return 'Unknown';
  };

  render() {
    const { classes, t, locationId } = this.props;
    const { loading, saving } = this.state;

    if (!locationId) {
      return (
        <Box className={classes.root}>
          <Typography>{t('Location.ColumnMapping.NoLocationSelected')}</Typography>
        </Box>
      );
    }

    // Only show loading if we're fetching column mappings
    if (loading) {
      return (
        <Box className={classes.root} sx={{ display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Box className={classes.root}>
        <Typography variant="h6" gutterBottom>
          {t('Location.ColumnMapping.Title')}
        </Typography>
        <Typography variant="body2" color="textSecondary" paragraph>
          {t('Location.ColumnMapping.Description')}
        </Typography>

        <Divider style={{ marginBottom: 16, marginTop: 16 }} />

        {this.renderColumnVisualization()}

        <DialogActions className={classes.buttonContainer} sx={{ padding: '8px 28px 8px 8px' }}>
          <Button
            onClick={this.props.onClose || (() => {})}
            color="primary"
            className={classes.primaryButton}
          >
            {this.props.operation === 'View'
              ? t('Location.CloseButton')
              : t('Location.CancelButton')}
          </Button>
          {this.props.operation !== 'View' && (
            <Button
              variant="contained"
              color="primary"
              onClick={this.handleSave}
              disabled={saving}
              className={classes.primaryButton}
            >
              {saving ? t('Location.ColumnMapping.Saving') : t('Location.ColumnMapping.Save')}
            </Button>
          )}
        </DialogActions>
      </Box>
    );
  }
}

ColumnMappingTab.propTypes = {
  locationId: PropTypes.string,
  isSuperAdmin: PropTypes.bool,
  userGroupType: PropTypes.string,
  expansions: PropTypes.array,
  boxTypeId: PropTypes.string,
  boxTypes: PropTypes.array,
  operation: PropTypes.string,
  onClose: PropTypes.func,
  onColumnMappingsSaved: PropTypes.func,
  columnMappings: PropTypes.array, // Array of { column_id, group_type_id }
  classes: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
};

export default withStyles(styles)(withTranslation()(ColumnMappingTab));



// WEBPACK FOOTER //
// ./src/components/box-list/ColumnMappingTab.js